import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { string, func, bool } from 'prop-types';

import { FIELDS, ID_TYPES } from '../../constants';
import styles from '../../styles.module.scss';

import Field from '~components/Field';
import InputLabel from '~components/InputLabel';
import { ANALYTICS_IDS } from '~constants/analytics';
import Dropdown from '~components/Dropdown';
import PhoneField from '~components/PhoneField';
import { companyPropTypes } from '~propTypes/company';
import { currentOrderPropTypes } from '~propTypes/orders';
import { COUNTRIES, CURRENT_COUNTRY_ID } from '~constants/environment';
import CustomButton from '~components/CustomButton';

function ElectronicReceiptFormLayout({
  isSubmitting,
  handleBlur,
  values,
  setFieldValue,
  submitForm,
  handleDeleteAddress,
  btnClassName,
  company
}) {
  const [idType, setIdType] = useState({ name: null, value: null });
  const currentUser = useSelector(state => state.auth.currentUser);

  useEffect(() => {
    const findTypeIdentification = ID_TYPES[CURRENT_COUNTRY_ID].find(
      type => type.value === company?.identificationType
    );
    if (findTypeIdentification) {
      setIdType(findTypeIdentification);
      setFieldValue(FIELDS.identificationType, findTypeIdentification?.value);
    }
  }, []);

  const handleChange = e => {
    setIdType(ID_TYPES[CURRENT_COUNTRY_ID].find(type => type.value === e.target.value));
  };

  return (
    <form className={cn('full-width', styles.container)} noValidate>
      <Field
        name={FIELDS.commercialName}
        component={InputLabel}
        inputType="text"
        label={t(`ElectronicReceiptForm:${FIELDS.commercialName}`)}
        className={styles.name}
      />
      <Field
        name={FIELDS.identificationType}
        component={Dropdown}
        inputType="text"
        label={t(`ElectronicReceiptForm:${FIELDS.identificationType}`)}
        value={idType?.value}
        options={ID_TYPES[CURRENT_COUNTRY_ID] || []}
        onChange={handleChange}
        className={`column middle full-width ${styles.dropdown}`}
        labelClassname={styles.optionsContainer}
        setFieldValue={setFieldValue}
        allowUniqueOption>
        <p
          className={cn({
            'text-alto': !idType?.name,
            'text-dove-gray': idType
          })}>
          {idType?.name || t('ElectronicReceiptForm:identificationType')}
        </p>
      </Field>
      <Field
        name={FIELDS.identificationNumber}
        component={InputLabel}
        inputType="text"
        label={t(`ElectronicReceiptForm:${FIELDS.identificationNumber}`)}
        className={`column ${styles.name}`}
      />
      <PhoneField
        key={FIELDS.commercialPhone}
        onBlur={handleBlur}
        fieldName={FIELDS.commercialPhone}
        values={values}
        setFieldValue={setFieldValue}
        className={`column ${styles.name}`}
        label={t(`ElectronicReceiptForm:${FIELDS.commercialPhone}`)}
      />
      {COUNTRIES.cr !== CURRENT_COUNTRY_ID && (
        <PhoneField
          key={FIELDS.faxNumber}
          onBlur={handleBlur}
          fieldName={FIELDS.faxNumber}
          values={values}
          setFieldValue={setFieldValue}
          className={`column ${styles.name}`}
          label={t(`ElectronicReceiptForm:${FIELDS.faxNumber}`)}
        />
      )}
      <Field
        name={FIELDS.tradeName}
        component={InputLabel}
        inputType="text"
        label={t(`ElectronicReceiptForm:${FIELDS.tradeName}`)}
        className={`column ${styles.name}`}
      />
      <Field
        name={FIELDS.commercialEmail}
        component={InputLabel}
        inputType="email"
        label={t(`ElectronicReceiptForm:${FIELDS.commercialEmail}`)}
        className={`column ${styles.name}`}
      />
      {[COUNTRIES.pt, COUNTRIES.es, COUNTRIES.gt, COUNTRIES.pa].includes(CURRENT_COUNTRY_ID) && (
        <>
          <Field
            name={FIELDS.commercialTextAddress}
            component={InputLabel}
            inputType="text"
            label={t(`ElectronicReceiptForm:${FIELDS.commercialTextAddress}`)}
            className={styles.name}
          />
          <Field
            name={FIELDS.commercialCity}
            component={InputLabel}
            inputType="text"
            label={t(`ElectronicReceiptForm:${FIELDS.commercialCity}`)}
            className={styles.name}
          />
          <Field
            name={FIELDS.commercialPostalCode}
            component={InputLabel}
            inputType="text"
            label={t(`ElectronicReceiptForm:${FIELDS.commercialPostalCode}`)}
            className={styles.name}
          />
        </>
      )}

      {company?.id && currentUser ? (
        <>
          <CustomButton
            type="button"
            onClick={submitForm}
            disabled={isSubmitting}
            id={ANALYTICS_IDS.checkout.saveElectronicReceipt}
            className={`button primary m-bottom-2 ${styles.saveButton} ${btnClassName}`}>
            {t('ElectronicReceiptForm:select')}
          </CustomButton>
          <CustomButton
            onClick={handleDeleteAddress}
            type="button"
            btnClassType="link"
            className={`button m-bottom-2 ${styles.deleteButton}`}>
            {t('ElectronicReceiptForm:delete')}
          </CustomButton>
        </>
      ) : (
        <CustomButton
          type="button"
          onClick={submitForm}
          disabled={isSubmitting}
          id={ANALYTICS_IDS.checkout.saveElectronicReceipt}
          className={`button primary m-bottom-2 ${styles.saveButton} ${btnClassName}`}>
          {t('ElectronicReceiptForm:save')}
        </CustomButton>
      )}
    </form>
  );
}

ElectronicReceiptFormLayout.propTypes = {
  handleBlur: func.isRequired,
  handleDeleteAddress: func.isRequired,
  setFieldValue: func.isRequired,
  submitForm: func.isRequired,
  btnClassName: string,
  company: companyPropTypes,
  isSubmitting: bool,
  values: currentOrderPropTypes
};

export default ElectronicReceiptFormLayout;
